/**
 * WarehouseMovementsPanel
 * /api/warehouse/analytics/stock_movements_trend/?days=14
 * Mini bar chart: stock_in (green) vs stock_out (orange) per day.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, RefreshCw, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const DAYS = 14;

const TYPE_CFG = {
  stock_in:    { label: 'In',         color: '#10B981' },
  stock_out:   { label: 'Out',        color: '#F97316' },
  adjustment:  { label: 'Adjustment', color: '#8B5CF6' },
  transfer:    { label: 'Transfer',   color: '#3B82F6' },
  return:      { label: 'Return',     color: '#14B8A6' },
};

const fmtDay = s => {
  const d = new Date(s);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const WarehouseMovementsPanel = () => {
  const [raw,     setRaw]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    axios.get(`${API_BASE}/api/warehouse/analytics/stock_movements_trend/`, {
      headers, params: { days: DAYS },
    })
      .then(r => setRaw(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Could not load movement data.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line

  // Build per-day grouped data
  const { dates, series, maxVal } = (() => {
    if (!raw.length) return { dates: [], series: {}, maxVal: 1 };

    // collect all dates in range
    const today = new Date(); today.setHours(0,0,0,0);
    const allDates = Array.from({ length: DAYS }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() - (DAYS - 1 - i));
      return d.toISOString().slice(0, 10);
    });

    const byDateType = {};
    raw.forEach(r => {
      const key = `${r.created_at__date}|${r.movement_type}`;
      byDateType[key] = (byDateType[key] || 0) + (r.total_quantity || 0);
    });

    const types = [...new Set(raw.map(r => r.movement_type))];
    const series = {};
    types.forEach(t => {
      series[t] = allDates.map(d => byDateType[`${d}|${t}`] || 0);
    });

    const maxVal = Math.max(1, ...Object.values(series).flat());
    return { dates: allDates, series, maxVal };
  })();

  const types = Object.keys(series);
  const BAR_W = 8;
  const CHART_H = 80;
  const GAP = 2;
  const groupW = types.length * (BAR_W + GAP) + 4;
  const totalW = dates.length * groupW;

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <TrendingUp size={13} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-slate-800">Stock Movements</span>
          <span className="text-[10px] text-slate-400">last {DAYS} days</span>
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && (
        <div className="flex-1 min-h-0 flex flex-col p-3 gap-3">
          {/* legend */}
          {types.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {types.map(t => {
                const cfg = TYPE_CFG[t] || { label: t, color: '#94a3b8' };
                return (
                  <div key={t} className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: cfg.color }} />
                    <span className="text-[10px] text-slate-500">{cfg.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* chart */}
          {dates.length === 0 || types.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-xs text-slate-300">No movements in this period</div>
          ) : (
            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
              style={{ scrollbarWidth: 'thin', scrollbarColor: '#e2e8f0 transparent' }}>
              <div style={{ width: Math.max(totalW, 200), height: '100%', minHeight: CHART_H + 24, position: 'relative' }}>
                {/* horizontal grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map(frac => (
                  <div key={frac} style={{
                    position: 'absolute',
                    left: 0, right: 0,
                    top: `${(1 - frac) * CHART_H}px`,
                    borderTop: '1px dashed #f1f5f9',
                  }} />
                ))}

                {/* bars */}
                {dates.map((date, di) => (
                  <div key={date} style={{
                    position: 'absolute',
                    left: di * groupW,
                    top: 0,
                    width: groupW,
                    height: CHART_H,
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: GAP,
                    paddingLeft: 2,
                  }}>
                    {types.map(t => {
                      const val = series[t][di];
                      const h   = val > 0 ? Math.max(3, Math.round((val / maxVal) * CHART_H)) : 0;
                      const cfg = TYPE_CFG[t] || { color: '#94a3b8' };
                      return (
                        <div key={t}
                          title={`${fmtDay(date)} · ${TYPE_CFG[t]?.label || t}: ${val}`}
                          style={{
                            width: BAR_W, height: h, borderRadius: '2px 2px 0 0',
                            backgroundColor: cfg.color,
                            opacity: val > 0 ? 1 : 0.15,
                            flexShrink: 0,
                          }} />
                      );
                    })}
                  </div>
                ))}

                {/* date labels — every 3rd day */}
                {dates.map((date, di) => di % 3 === 0 && (
                  <div key={date} style={{
                    position: 'absolute',
                    left: di * groupW,
                    top: CHART_H + 4,
                    width: groupW * 3,
                    fontSize: 9,
                    color: '#94a3b8',
                    whiteSpace: 'nowrap',
                  }}>
                    {fmtDay(date)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WarehouseMovementsPanel;
